import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../api/axiosInstance";
import { formatDateForDisplay } from "../utils/formatters";
import {
  SLICE_CONFIG,
  HOUSEHOLD_METRICS_SLICE_CONFIG,
  TRANSITION_HEALTH_SLICE_CONFIG,
  ACCOUNT_METRICS_SLICE_CONFIG,
  TRANSITION_STAGE_STYLES,
  TRANSITION_BREAKDOWN_OVERRIDES,
  DEFAULT_GROUP_STYLE,
} from "../constants/projectInsightsConstants";

ChartJS.register(ArcElement, Tooltip, Legend);

const formatAUM = (value) => {
  if (value == null) return "$0";
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const centerTextPlugin = {
  id: "centerText",
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const meta = chart.options.plugins.centerText;
    if (!meta) return;

    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.font = "bold 36px system-ui, -apple-system, sans-serif";
    ctx.fillStyle = meta.totalColor || "#111827";
    ctx.fillText(meta.total ?? "", centerX, centerY - 18);

    ctx.font = "600 14px system-ui, -apple-system, sans-serif";
    ctx.fillStyle = "#6b7280";
    ctx.fillText(meta.label || "Households", centerX, centerY + 10);

    let badgeY = centerY + 32;
    if (meta.critical > 0) {
      ctx.font = "bold 13px system-ui, -apple-system, sans-serif";
      ctx.fillStyle = "#dc2626";
      ctx.fillText(`${meta.critical} Critical`, centerX, badgeY);
      badgeY += 18;
    }
    if (meta.handHolding > 0) {
      ctx.font = "bold 13px system-ui, -apple-system, sans-serif";
      ctx.fillStyle = "#d97706";
      ctx.fillText(`${meta.handHolding} Hand-Holding Needed`, centerX, badgeY);
    }

    ctx.restore();
  }
};

ChartJS.register(centerTextPlugin);

function buildChartData(households) {
  const data = SLICE_CONFIG.map(s => households?.[s.key] || 0);
  return {
    labels: SLICE_CONFIG.map(s => s.label),
    datasets: [{
      data,
      backgroundColor: SLICE_CONFIG.map(s => s.color),
      hoverBackgroundColor: SLICE_CONFIG.map(s => s.hoverColor),
      borderWidth: 2,
      borderColor: "#ffffff",
      hoverBorderColor: "#ffffff",
      hoverOffset: 6
    }]
  };
}

function buildChartOptions(total, critical, handHolding) {
  return {
    responsive: true,
    maintainAspectRatio: true,
    cutout: "62%",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1f2937",
        titleFont: { size: 13, weight: "bold" },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.raw} household${ctx.raw !== 1 ? "s" : ""}`
        }
      },
      centerText: { total, critical, handHolding }
    }
  };
}

function buildHouseholdMetricsData(households) {
  const data = HOUSEHOLD_METRICS_SLICE_CONFIG.map(s => households?.[s.key] || 0);
  return {
    labels: HOUSEHOLD_METRICS_SLICE_CONFIG.map(s => s.label),
    datasets: [{
      data,
      backgroundColor: HOUSEHOLD_METRICS_SLICE_CONFIG.map(s => s.color),
      hoverBackgroundColor: HOUSEHOLD_METRICS_SLICE_CONFIG.map(s => s.hoverColor),
      borderWidth: 2,
      borderColor: "#ffffff",
      hoverBorderColor: "#ffffff",
      hoverOffset: 6
    }]
  };
}

function buildHouseholdMetricsOptions(total, critical, handHolding) {
  return {
    responsive: true,
    maintainAspectRatio: true,
    cutout: "62%",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1f2937",
        titleFont: { size: 13, weight: "bold" },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.raw} household${ctx.raw !== 1 ? "s" : ""}`
        }
      },
      centerText: { total, critical, handHolding }
    }
  };
}

function buildTransitionHealthData(transitionHealth) {
  const data = TRANSITION_HEALTH_SLICE_CONFIG.map(s => transitionHealth?.[s.key] || 0);
  return {
    labels: TRANSITION_HEALTH_SLICE_CONFIG.map(s => s.label),
    datasets: [{
      data,
      backgroundColor: TRANSITION_HEALTH_SLICE_CONFIG.map(s => s.color),
      hoverBackgroundColor: TRANSITION_HEALTH_SLICE_CONFIG.map(s => s.hoverColor),
      borderWidth: 2,
      borderColor: "#ffffff",
      hoverBorderColor: "#ffffff",
      hoverOffset: 6
    }]
  };
}

function buildTransitionHealthOptions(total, critical, handHolding) {
  return {
    responsive: true,
    maintainAspectRatio: true,
    cutout: "62%",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1f2937",
        titleFont: { size: 13, weight: "bold" },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.raw} household${ctx.raw !== 1 ? "s" : ""}`
        }
      },
      centerText: { total, critical, handHolding }
    }
  };
}

function buildAccountMetricsData(accounts) {
  const data = ACCOUNT_METRICS_SLICE_CONFIG.map(s => accounts?.[s.key] || 0);
  return {
    labels: ACCOUNT_METRICS_SLICE_CONFIG.map(s => s.label),
    datasets: [{
      data,
      backgroundColor: ACCOUNT_METRICS_SLICE_CONFIG.map(s => s.color),
      hoverBackgroundColor: ACCOUNT_METRICS_SLICE_CONFIG.map(s => s.hoverColor),
      borderWidth: 2,
      borderColor: "#ffffff",
      hoverBorderColor: "#ffffff",
      hoverOffset: 6
    }]
  };
}

function buildAccountMetricsOptions(total) {
  return {
    responsive: true,
    maintainAspectRatio: true,
    cutout: "62%",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1f2937",
        titleFont: { size: 13, weight: "bold" },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.raw} account${ctx.raw !== 1 ? "s" : ""}`
        }
      },
      centerText: { total, label: "Accounts", critical: 0, handHolding: 0 }
    }
  };
}

function SummaryBox({ title, icon, items, accent = "emerald" }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-7">
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-11 h-11 rounded-xl bg-${accent}-100 flex items-center justify-center`}>
          <span className="text-xl">{icon}</span>
        </div>
        <h3 className="text-base font-bold text-gray-800 uppercase tracking-wider">{title}</h3>
      </div>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between">
            <span className={`text-base ${item.highlight ? "font-bold" : "font-medium"} ${item.color || "text-gray-600"}`}>
              {item.prefix && <span className="mr-1">{item.prefix}</span>}
              {item.label}
            </span>
            <span className={`text-base font-bold ${item.valueColor || "text-gray-900"}`}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartLegend() {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center mt-4">
      {SLICE_CONFIG.map(s => (
        <div key={s.key} className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }}></div>
          <span className="text-xs font-medium text-gray-600">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function DonutCard({ title, subtitle, households, transitionHealth, aum, showAum, isOverall }) {
  const chartData = buildChartData(households);
  const options = buildChartOptions(
    households?.total || 0,
    transitionHealth?.stale_household || 0,
    transitionHealth?.hand_holding_required || 0
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col min-w-[400px]">
      <div className="px-6 pt-6 pb-3 border-b border-gray-100">
        <h3 className="font-bold text-gray-900 text-xl">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>

      {!isOverall && (
        <div className="px-6 pt-4 grid grid-cols-2 gap-x-5 gap-y-2 text-sm">
          {showAum && aum?.total_aum != null && (
            <div className="flex justify-between">
              <span className="text-gray-500">AUM</span>
              <span className="font-bold text-gray-800">{formatAUM(aum.total_aum)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-500">Households</span>
            <span className="font-bold text-gray-800">{households?.total || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Completed</span>
            <span className="font-bold text-emerald-700">
              {households?.total ? `${Math.round(((households.completed_households || 0) / households.total) * 100)}%` : "0%"}
            </span>
          </div>
          {transitionHealth?.stale_household > 0 && (
            <div className="flex justify-between">
              <span className="text-red-600 font-bold">Critical Pending</span>
              <span className="font-bold text-red-700">{transitionHealth.stale_household}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-80 h-80">
          <Doughnut data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
}

function HouseholdMetricsLegend() {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center mt-4">
      {HOUSEHOLD_METRICS_SLICE_CONFIG.map(s => (
        <div key={s.key} className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }}></div>
          <span className="text-xs font-medium text-gray-600">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function HouseholdMetricsDonutCard({ title, households, transitionHealth }) {
  const chartData = buildHouseholdMetricsData(households);
  const options = buildHouseholdMetricsOptions(
    households?.total || 0,
    transitionHealth?.stale_household || 0,
    transitionHealth?.hand_holding_required || 0
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col min-w-[400px]">
      <div className="px-6 pt-6 pb-3 border-b border-gray-100">
        <h3 className="font-bold text-gray-900 text-xl">{title}</h3>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-80 h-80">
          <Doughnut data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
}

function TransitionHealthLegend() {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center mt-4">
      {TRANSITION_HEALTH_SLICE_CONFIG.map(s => (
        <div key={s.key} className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }}></div>
          <span className="text-xs font-medium text-gray-600">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function TransitionHealthDonutCard({ title, transitionHealth }) {
  const chartData = buildTransitionHealthData(transitionHealth);
  const options = buildTransitionHealthOptions(
    transitionHealth?.total_households || 0,
    transitionHealth?.stale_household || 0,
    transitionHealth?.hand_holding_required || 0
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col min-w-[400px]">
      <div className="px-6 pt-6 pb-3 border-b border-gray-100">
        <h3 className="font-bold text-gray-900 text-xl">{title}</h3>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-80 h-80">
          <Doughnut data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
}

function AccountMetricsLegend() {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center mt-4">
      {ACCOUNT_METRICS_SLICE_CONFIG.map(s => (
        <div key={s.key} className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }}></div>
          <span className="text-xs font-medium text-gray-600">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function AccountMetricsDonutCard({ title, accounts }) {
  const chartData = buildAccountMetricsData(accounts);
  const options = buildAccountMetricsOptions(accounts?.total || 0);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col min-w-[400px]">
      <div className="px-6 pt-6 pb-3 border-b border-gray-100">
        <h3 className="font-bold text-gray-900 text-xl">{title}</h3>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-80 h-80">
          <Doughnut data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
}

function TransitionProgressChart({ groups, showAum }) {
  const allAums = groups.flatMap(g => [g.aum_dollars || 0, ...(g.children || []).map(c => c.aum_dollars || 0)]);
  const maxAum = Math.max(...allAums, 1);

  const renderRow = ({ key, label, aum, count, barClass, isChild, isBold }) => {
    const widthPct = Math.max((aum / maxAum) * 100, aum > 0 ? 4 : 0);
    return (
      <div key={key} className="grid grid-cols-12 items-center gap-3">
        <div className={`col-span-3 text-sm truncate ${isBold ? "font-bold text-gray-900" : "text-gray-700"} ${isChild ? "pl-6" : ""}`} title={label}>
          {isChild && <span className="text-gray-400 mr-1.5">↳</span>}
          {label}
        </div>
        <div className={`col-span-1 text-sm text-right whitespace-nowrap font-semibold ${isBold ? "text-gray-900" : "text-gray-800"}`}>
          {count}
        </div>
        <div className="col-span-6">
          <div className="relative h-6 bg-gray-100 rounded-md overflow-hidden">
            {aum > 0 && (
              <div
                className={`h-full ${barClass} rounded-md transition-all`}
                style={{ width: `${widthPct}%` }}
              />
            )}
          </div>
        </div>
        <div className="col-span-2 text-sm whitespace-nowrap text-right">
          {showAum && (
            <span className="font-semibold text-gray-900">{formatAUM(aum)}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div className="text-sm font-semibold text-gray-700">
          Transition progress across status types
          {showAum && <span className="text-gray-400 font-normal"> · count and AUM</span>}
        </div>
      </div>

      <div className="space-y-2.5">
        {groups.map(group => {
          const style = TRANSITION_STAGE_STYLES[group.key] || DEFAULT_GROUP_STYLE;
          return (
            <React.Fragment key={group.key}>
              {renderRow({
                key: group.key,
                label: group.label,
                aum: group.aum_dollars || 0,
                count: group.household_count || 0,
                barClass: style.parent,
                isChild: false,
                isBold: !!group.is_bold,
              })}
              {(group.children || []).map(child =>
                renderRow({
                  key: `${group.key}.${child.key}`,
                  label: child.label,
                  aum: child.aum_dollars || 0,
                  count: child.household_count || 0,
                  barClass: TRANSITION_BREAKDOWN_OVERRIDES[child.key] || style.child,
                  isChild: true,
                  isBold: false,
                })
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function OverallCompletionCard({ completion, showAum }) {
  const byVolume = completion?.by_volume_pct ?? 0;
  const byAum = completion?.by_aum_pct ?? 0;
  const completedHHs = completion?.completed_households ?? 0;
  const totalHHs = completion?.total_households ?? 0;
  const completedAum = completion?.completed_aum_dollars ?? 0;
  const totalAum = completion?.total_aum_dollars ?? 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-5">Overall project completion</h3>
      <div className={`grid gap-6 ${showAum ? "grid-cols-2" : "grid-cols-1"}`}>
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">By Volume</div>
          <div className="text-4xl font-extrabold text-gray-900 leading-none mb-3">{byVolume.toFixed(0)}%</div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all"
              style={{ width: `${Math.min(byVolume, 100)}%` }}
            />
          </div>
          <div className="text-sm text-gray-600">
            {completedHHs} of {totalHHs} Households completed
          </div>
        </div>
        {showAum && (
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">By AUM</div>
            <div className="text-4xl font-extrabold text-gray-900 leading-none mb-3">{byAum.toFixed(0)}%</div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${Math.min(byAum, 100)}%` }}
              />
            </div>
            <div className="text-sm text-gray-600">
              {formatAUM(completedAum)} of {formatAUM(totalAum)} transferred
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OutstandingIssuesPanel({ nigoIssues }) {
  const totalCount = nigoIssues?.total_count || 0;
  const rows = nigoIssues?.rows || [];
  const maxCount = Math.max(...rows.map(r => r.household_count || 0), 1);
  const avg = nigoIssues?.avg_age_days;
  const oldest = nigoIssues?.oldest_age_days;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">Outstanding issues</h3>

      {/* Banner card */}
      <div className="flex items-center gap-5 bg-red-50 rounded-xl px-6 py-5 mb-5">
        <div className="text-5xl font-extrabold text-red-800 leading-none w-16 text-center">
          {totalCount}
        </div>
        <div className="flex-1">
          <div className="font-bold text-gray-900 text-base">Open NIGOs across the project</div>
          <div className="text-sm text-gray-600 mt-0.5">
            {totalCount > 0
              ? <>Avg age: <span className="font-medium text-gray-800">{Math.round(avg ?? 0)} days</span><span className="mx-2 text-gray-300">·</span>Oldest: <span className="font-medium text-gray-800">{oldest ?? 0} days</span></>
              : <>No open NIGOs.</>}
          </div>
        </div>
      </div>

      {/* Sub-level breakdown */}
      <div className="space-y-2.5 px-2">
        {rows.map(row => {
          const count = row.household_count || 0;
          const widthPct = count > 0 ? Math.max((count / maxCount) * 100, 6) : 0;
          return (
            <div key={row.key} className="grid grid-cols-12 items-center gap-3">
              <div className="col-span-3 text-sm text-gray-700">{row.label}</div>
              <div className="col-span-8">
                <div className="relative h-5 bg-gray-100 rounded-full overflow-hidden">
                  {count > 0 && (
                    <div
                      className="h-full bg-red-700 rounded-full transition-all"
                      style={{ width: `${widthPct}%` }}
                    />
                  )}
                </div>
              </div>
              <div className="col-span-1 text-sm font-bold text-gray-900 text-right">
                {count}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProjectInsights({ user }) {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [projRes, insightsRes] = await Promise.all([
        axiosInstance.get(`/projects/${projectId}`),
        axiosInstance.get(`/projects/${projectId}/insights`)
      ]);
      setProject(projRes.data);
      setInsights(insightsRes.data);
    } catch (err) {
      console.error("Error fetching insights:", err);
      setError(err.response?.data?.detail || "Failed to load insights");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showAum = project?.aum_availability !== 3;
  const overall = insights?.overall;
  const byAdvisor = insights?.by_advisor || {};

  const resolveAdvisorName = (advisorKey) => {
    if (!project?.advisors) return advisorKey;
    const byId = project.advisors.find(a => a.user_id === advisorKey || a.id === advisorKey);
    if (byId) return `${byId.first_name || ""} ${byId.last_name || ""}`.trim();
    const idx = parseInt(advisorKey, 10);
    if (!isNaN(idx) && project.advisors[idx]) {
      const a = project.advisors[idx];
      return `${a.first_name || ""} ${a.last_name || ""}`.trim();
    }
    return advisorKey;
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
        <Header user={user} />
        <div className="flex flex-1">
          <Sidebar />
          <main className="flex-1 p-8 flex items-center justify-center">
            <div className="text-center">
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-transparent border-t-emerald-600 rounded-full animate-spin"></div>
              </div>
              <p className="text-gray-600 font-medium text-lg">Loading project insights...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
        <Header user={user} />
        <div className="flex flex-1">
          <Sidebar />
          <main className="flex-1 p-8 flex items-center justify-center">
            <div className="text-center bg-white rounded-2xl p-8 shadow-lg border border-red-100 max-w-md">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Failed to Load Insights</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button onClick={() => navigate(`/project/${projectId}`)} className="btn-primary px-6 py-2">
                Back to Project
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
      <Header user={user} />
      <div className="flex flex-1">
        <Sidebar />
        <main className="flex-1 p-8 bg-gradient-to-b from-transparent to-emerald-50/10 overflow-y-auto">
          <div className="max-w-[1400px] mx-auto w-full">

            {/* Project Header */}
            <div className="mb-8 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-blue-500/5 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-emerald-100/50 shadow-xl">
                <button
                  onClick={() => navigate(`/project/${projectId}`)}
                  className="mb-6 flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold transition-all hover:gap-3 group"
                >
                  <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Project
                </button>

                <div className="flex items-center gap-4 mb-3">
                  <div className="flex-1">
                    <h1 className="text-3xl font-extrabold bg-gradient-to-r from-gray-900 via-emerald-700 to-gray-900 bg-clip-text text-transparent mb-2">
                      Project Insights
                    </h1>
                    <p className="text-gray-600 text-lg font-medium">{project?.title}</p>
                  </div>
                  {project?.status && (
                    <span className={`badge px-3 py-1 font-medium ${
                      project.status === "completed" ? "badge-success" :
                      project.status === "initiated" || project.status === "created" ? "badge-info" :
                      "badge-warning"
                    }`}>
                      {project.status === "created" ? "Ongoing" : project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </span>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-emerald-200/50">
                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Destination Custodian</h3>
                      <div className="px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-50 text-emerald-700">
                        {project?.custodian || "Fidelity"}
                      </div>
                    </div>
                    <div className="h-4 w-px bg-gray-300"></div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Destination Firm</h3>
                      <div className="px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-50 text-emerald-700">
                        {project?.destination_firm || "N/A"}
                      </div>
                    </div>
                    {project?.target_end_date && (
                      <>
                        <div className="h-4 w-px bg-gray-300"></div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Target End Date</h3>
                          <div className="px-3 py-2 rounded-lg text-sm font-semibold bg-amber-50 text-amber-700">
                            {formatDateForDisplay(project.target_end_date)}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

             {/* ── Section 1.6: Transition Progress ── */}
            {insights?.transition_progress?.groups?.some(g => g.household_count > 0) && (
              <>
                <hr className="border-t border-gray-200 my-8" />
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Transition Progress</h2>
                  <TransitionProgressChart groups={insights.transition_progress.groups} showAum={showAum} />
                </div>
              </>
            )}

            {/* ── Section 1.5: Insights overview (completion + outstanding issues) ── */}
            <hr className="border-t border-gray-200 my-8" />
            <div className="mb-8">
              <div className="text-center mb-5">
                <h2 className="text-2xl font-bold text-gray-900">Insights</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Project-level analytics &middot; click anywhere to drill into the underlying Households
                </p>
              </div>
              <div className="grid gap-5 grid-cols-1 lg:grid-cols-2">
                <OverallCompletionCard completion={insights?.completion} showAum={showAum} />
                <OutstandingIssuesPanel nigoIssues={insights?.transition_progress?.nigo_issues} />
              </div>
            </div>

           

            {/* ── Divider ── */}
            <hr className="border-t border-gray-200 my-8" />

            {/* ── Section 2: Transition Overview ── */}
            {overall && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Transition Overview</h2>

                <ChartLegend />

                {/* Overall Chart - Centred */}
                <div className="mt-6 flex justify-center">
                  <DonutCard
                    title="Overall Chart"
                    subtitle="All advisors combined"
                    households={overall.households}
                    transitionHealth={overall.transition_health}
                    aum={overall.aum}
                    showAum={showAum}
                    isOverall
                  />
                </div>

                {/* Per-Advisor Charts - 2 per row, last odd one centred */}
                {Object.keys(byAdvisor).length > 0 && (() => {
                  const entries = Object.entries(byAdvisor);
                  const isOdd = entries.length % 2 !== 0;
                  return (
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 justify-items-center">
                      {entries.map(([advisorId, data], idx) => (
                        <div
                          key={advisorId}
                          className={isOdd && idx === entries.length - 1 ? "md:col-span-2 flex justify-center" : "w-full flex justify-center"}
                        >
                          <DonutCard
                            title={resolveAdvisorName(advisorId)}
                            households={data.households}
                            transitionHealth={data.transition_health}
                            aum={data.aum}
                            showAum={showAum}
                            isOverall={false}
                          />
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── Divider ── */}
            <hr className="border-t border-gray-200 my-8" />

            {/* ── Section 3: Per Advisor Household Metrics ── */}
            {Object.keys(byAdvisor).length > 0 && (() => {
              const entries = Object.entries(byAdvisor);
              const isOdd = entries.length % 2 !== 0;
              return (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Per Advisor Household Metrics</h2>

                  <HouseholdMetricsLegend />

                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 justify-items-center">
                    {entries.map(([advisorId, data], idx) => (
                      <div
                        key={advisorId}
                        className={isOdd && idx === entries.length - 1 ? "md:col-span-2 flex justify-center" : "w-full flex justify-center"}
                      >
                        <HouseholdMetricsDonutCard
                          title={resolveAdvisorName(advisorId)}
                          households={data.households}
                          transitionHealth={data.transition_health}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ── Divider ── */}
            <hr className="border-t border-gray-200 my-8" />

            {/* ── Section 4: Per Advisor Transition Health Metrics ── */}
            {Object.keys(byAdvisor).length > 0 && (() => {
              const entries = Object.entries(byAdvisor);
              const isOdd = entries.length % 2 !== 0;
              return (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Per Advisor Transition Health Metrics</h2>

                  <TransitionHealthLegend />

                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 justify-items-center">
                    {entries.map(([advisorId, data], idx) => (
                      <div
                        key={advisorId}
                        className={isOdd && idx === entries.length - 1 ? "md:col-span-2 flex justify-center" : "w-full flex justify-center"}
                      >
                        <TransitionHealthDonutCard
                          title={resolveAdvisorName(advisorId)}
                          transitionHealth={data.transition_health}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ── Divider ── */}
            <hr className="border-t border-gray-200 my-8" />

            {/* ── Section 5: Per Advisor Account Metrics ── */}
            {Object.keys(byAdvisor).length > 0 && (() => {
              const entries = Object.entries(byAdvisor);
              const isOdd = entries.length % 2 !== 0;
              return (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Per Advisor Account Metrics</h2>

                  <AccountMetricsLegend />

                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 justify-items-center">
                    {entries.map(([advisorId, data], idx) => (
                      <div
                        key={advisorId}
                        className={isOdd && idx === entries.length - 1 ? "md:col-span-2 flex justify-center" : "w-full flex justify-center"}
                      >
                        <AccountMetricsDonutCard
                          title={resolveAdvisorName(advisorId)}
                          accounts={data.accounts}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ── Section 1: Summary ── */}
            {overall && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Summary</h2>
                <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                  {showAum && (
                    <SummaryBox
                      title="AUM"
                      icon="💰"
                      accent="emerald"
                      items={[
                        { label: "Total AUM Tracked", value: formatAUM(overall.aum?.total_aum), highlight: true },
                        { label: "Households in Transition", value: overall.aum?.hh_in_transition ?? 0 },
                        { label: "Households Forms Downloaded", value: overall.aum?.hh_moved ?? 0 },
                        { label: "Transfer Rate", value: `${(overall.aum?.transfer_rate ?? 0).toFixed(1)}%`, valueColor: "text-emerald-700" }
                      ]}
                    />
                  )}
                  <SummaryBox
                    title="Households"
                    icon="🏠"
                    accent="blue"
                    items={[
                      { label: "Total Households", value: overall.households?.total ?? 0, highlight: true },
                      { label: "Not Started", value: overall.households?.not_started ?? 0 },
                      { label: "In Progress", value: overall.households?.in_progress ?? 0 },
                      { label: "Hand-Holding Needed", value: overall.households?.hand_holding_required ?? 0, highlight: true, color: "text-amber-600", valueColor: "text-amber-700" },
                      { label: "Completed", value: overall.households?.completed ?? 0, valueColor: "text-emerald-700" }
                    ]}
                  />
                  <SummaryBox
                    title="Transition Health"
                    icon="⚡"
                    accent="amber"
                    items={[
                      { label: "Total Households", value: overall.transition_health?.total_households ?? 0, highlight: true },
                      { label: "Households In Progress", value: overall.transition_health?.in_progress ?? 0 },
                      { label: "Pending with Advisor", value: overall.transition_health?.pending_on_advisor ?? 0 },
                      {
                        label: "Critical Pending",
                        value: overall.transition_health?.stale_household ?? 0,
                        highlight: true,
                        color: (overall.transition_health?.stale_household || 0) > 0 ? "text-red-600" : "text-gray-600",
                        valueColor: (overall.transition_health?.stale_household || 0) > 0 ? "text-red-700" : "text-gray-900"
                      },
                      { label: "Hand-Holding Needed", value: overall.transition_health?.hand_holding_required ?? 0, highlight: true, color: "text-amber-600", valueColor: "text-amber-700" }
                    ]}
                  />
                  <SummaryBox
                    title="Accounts"
                    icon="📋"
                    accent="indigo"
                    items={[
                      { label: "Total Accounts", value: overall.accounts?.total ?? 0, highlight: true },
                      { label: "Needs Transition", value: overall.accounts?.needs_transition ?? 0 },
                      { label: "Not Transitioning", value: overall.accounts?.not_transitioning ?? 0 }
                    ]}
                  />
                </div>
              </div>
            )}

            {/* ── Divider ── */}
            <hr className="border-t border-gray-200 my-8" />

          </div>
        </main>
      </div>
    </div>
  );
}

export default ProjectInsights;
